import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import UsersManagementGrid from "../components/UsersManagementGrid";
import CloneUser from "../components/CloneUser";
import RenderOnRole from "../components/RenderOnRole";
import { useChosenEmployer } from "../contexts/EmployerContext";

async function fetchEmployer() {
  const response = await HttpService.getAxiosClient().get(
    window.API_URL + `/employer/all?excludeRelations=true`
  );
  return response;
}

export default function UsersManagement() {
  const { employerData, chosenEmployerId } = useChosenEmployer();
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [cloneUserDialogOpen, setCloneUserDialogOpen] = useState(false);

  // Only fetch all employers when user has "ANY" access
  const { data: employersData, isLoading: isLoadingEmployers } = useQuery(
    ["employer"],
    fetchEmployer,
    { enabled: chosenEmployerId === "ANY" }
  );

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Users Management
        </Typography>
        <RenderOnRole roles={["it-super-admin"]}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => setCloneUserDialogOpen(true)}
          >
            Clone User
          </Button>
        </RenderOnRole>
      </Box>

      {chosenEmployerId !== "ANY" ? (
        <UsersManagementGrid
          employerId={employerData.idEmployer}
          employer={employerData}
          showAddButton={true}
          showSubDepartmentFilter={true}
        />
      ) : (
        <>
          {/* Employer Selection */}
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            fullWidth
            options={employersData?.data || []}
            getOptionLabel={(option) => `${option.description}-[${option.idEmployer}]`}
            value={selectedEmployer}
            onChange={(event, newValue) => {
              setSelectedEmployer(newValue);
            }}
            loading={isLoadingEmployers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Company"
                placeholder="Choose a company to view users"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingEmployers ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option.idEmployer === value.idEmployer
            }
          />
        </Box>

          {/* Reusable Users Grid */}
          {selectedEmployer ? (
            <UsersManagementGrid
              key={selectedEmployer.idEmployer}
              employerId={selectedEmployer.idEmployer}
              employer={selectedEmployer}
              showAddButton={true}
              showSubDepartmentFilter={true}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <PeopleIcon sx={{ fontSize: 64, color: "text.secondary" }} />
              <Typography variant="h6" color="text.secondary">
                Select a company to view and manage users
              </Typography>
            </Box>
          )}
        </>
      )}
      <RenderOnRole roles={["it-super-admin"]}>
        <CloneUser
          open={cloneUserDialogOpen}
          setOpen={setCloneUserDialogOpen}
        />
      </RenderOnRole>
    </Box>
  );
}
